import { FilterFilled, FilterOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  Badge,
  Button,
  Checkbox,
  DatePicker,
  Divider,
  Empty,
  Form,
  Tag,
  Tooltip,
} from 'antd';
import { filter, forEach, isEmpty, map } from 'lodash';
import { React, useContext, useEffect, useState } from 'react';
import { AppContext } from '../../../AppContext';
import { ReactComponent as FilterIcon } from '../../../assets/svg/sliders-solid.svg';
import {
  DATE_FORMATS,
  LIMIT,
  REQUEST_STATUS,
  REQUEST_TYPES,
  SORT,
  STATUS_FILTER,
} from '../../../common/constants';
import { formatDate, hasNonEmptyValueObj } from '../../../common/utils';
import CommonTable from '../../../components/CommonTable';
import LoaderComponent from '../../../components/LoaderComponent';
import SearchComponent from '../../../components/SearchComponent';
import useInfiniteScroll from '../../../hooks/useInfiniteScroll';
import { CREATE_REQUEST_LIBRARY } from '../graphql/Mutations';
import { CLOSURE_REQUESTS_LIBRARY } from '../graphql/Queries';
import AllFilterClosureReqModal from './AllFilterClosureReqModal';
import ClosureRequestList from './ClosureRequestList';
import RaiseClosureRequestModal from './RaiseClosureRequestModal';

const { RangePicker } = DatePicker;

const initialPaginationValue = {
  total: 0,
  current: 1,
  pageSize: 10,
};

const initialAddressFilter = {
  limit: 10,
  search: '',
  skip: 0,
};

const initialAddressSort = {
  field: 'createdAt',
  order: 'DESC',
};

const ClosureRequestsTable = ({
  isRaiseRequestModalOpen,
  setIsRaiseRequestModalOpen,
}) => {
  const {
    state: { isDesktop },
  } = useContext(AppContext);
  const [form] = Form.useForm();
  const [closureReqList, setClosureReqList] = useState([]);
  const [closureReqSearchTerm, setClosureReqSearchTerm] = useState('');
  const [isEmptyClosureReqList, setIsEmptyClosureReqList] = useState(false);
  const [query, setQuery] = useState('');
  const [sortedInfo, setSortedInfo] = useState({});
  const [paginationProp, setPaginationProp] = useState(initialPaginationValue);

  const [isClosureReqLoading, setIsClosureReqLoading] = useState(true);
  const [filterList, setFilterList] = useState([]);
  const [whereFilter, setWhereFilter] = useState();
  const [filterLoading, setFilterLoading] = useState(false);
  const [filterTags, setFilterTags] = useState(null);
  const [filterVisible, setFilterVisible] = useState(false);
  const [filterIndex, setFilterIndex] = useState(null);
  const [finalWhereFilters, setFinalWhereFilters] = useState();
  const [finalFilterTags, setFinalFilterTags] = useState(null);
  const [isEmptyFilterList, setIsEmptyFilterList] = useState(false);
  const [isRaiseRequestLoading, setIsRaiseRequestLoading] = useState(false);

  const [isAllFilterModalOpen, setIsAllFilterModalOpen] = useState(false);
  const [
    isFetchMoreClosureReqLoading,
    setIsFetchMoreClosureReqLoading,
  ] = useState(false);
  const [isActive, setIsActive] = useState(true);

  const [requestsLibrary] = useLazyQuery(CLOSURE_REQUESTS_LIBRARY, {
    onCompleted: (response) => {
      if (isDesktop) {
        setClosureReqList([...response?.requestsLibrary?.data]);
      } else {
        setClosureReqList([
          ...closureReqList,
          ...response?.requestsLibrary?.data,
        ]);
      }
      if (
        response?.requestsLibrary?.count === 0 &&
        initialPaginationValue?.total === 0
      ) {
        setIsEmptyClosureReqList(true);
      } else {
        setIsEmptyClosureReqList(false);
      }
      if (response?.requestsLibrary?.data?.length < LIMIT) {
        setIsActive(false);
      }
      const pagination = {
        ...paginationProp,
        defaultPageSize: LIMIT,
        total: response?.requestsLibrary?.count,
      };
      setPaginationProp(pagination);
      setIsClosureReqLoading(false);
      setIsFetchMoreClosureReqLoading(false);
    },
    fetchPolicy: 'network-only',
    onError() {},
  });

  const [createRequestLibrary] = useMutation(CREATE_REQUEST_LIBRARY, {
    onError: () => {},
  });

  useEffect(() => {
    setIsClosureReqLoading(true);
    setClosureReqList([]);
    setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
    requestsLibrary({
      variables: {
        filter: {
          ...initialAddressFilter,
          limit: LIMIT,
          type: REQUEST_TYPES?.LIBRARY_CLOSURE,
          dateRange: {
            from: finalWhereFilters?.createdAt?.from?.$d,
            to: finalWhereFilters?.createdAt?.to?.$d,
          },
          actionTakenOn: {
            from: finalWhereFilters?.actionTakenOn?.from?.$d,
            to: finalWhereFilters?.actionTakenOn?.to?.$d,
          },
          status:
            finalWhereFilters?.status?.length > 0
              ? finalWhereFilters?.status
              : undefined,
          libraryIds: finalWhereFilters?.name,
          backUpLibraryIds: finalWhereFilters?.backUpLibName,
          closureRequestToDateRange: {
            to: finalWhereFilters?.toDate?.date?.$d,
          },
          closureRequestFromDateRange: {
            from: finalWhereFilters?.fromDate?.date?.$d,
          },
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialAddressSort,
      },
    });
  }, [isDesktop]);

  const handleTableChange = (pagination, tableFilter, sorter) => {
    const { current } = pagination;
    const skip = (current - 1) * (pagination?.pageSize || 0);
    setPaginationProp({ ...paginationProp, ...pagination });
    setIsClosureReqLoading(true);
    setSortedInfo(sorter);
    requestsLibrary({
      variables: {
        filter: {
          ...initialAddressFilter,
          skip,
          limit: pagination?.pageSize,
          search: closureReqSearchTerm,
          type: REQUEST_TYPES?.LIBRARY_CLOSURE,
          dateRange: {
            from: finalWhereFilters?.createdAt?.from?.$d,
            to: finalWhereFilters?.createdAt?.to?.$d,
          },
          actionTakenOn: {
            from: finalWhereFilters?.actionTakenOn?.from?.$d,
            to: finalWhereFilters?.actionTakenOn?.to?.$d,
          },
          status:
            finalWhereFilters?.status?.length > 0
              ? finalWhereFilters?.status
              : undefined,
          libraryIds: finalWhereFilters?.name,
          backUpLibraryIds: finalWhereFilters?.backUpLibName,
          closureRequestToDateRange: {
            to: finalWhereFilters?.toDate?.date?.$d,
          },
          closureRequestFromDateRange: {
            from: finalWhereFilters?.fromDate?.date?.$d,
          },
        },
        sort: sorter?.column
          ? {
              field: sorter?.field,
              order: sorter?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialAddressSort,
      },
    });
  };

  const handleSearch = (value) => {
    const trimValue = value?.trim();
    setClosureReqSearchTerm(trimValue);
    setClosureReqList([]);
    setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
    setIsClosureReqLoading(true);
    requestsLibrary({
      variables: {
        filter: {
          ...initialAddressFilter,
          limit: paginationProp?.pageSize || LIMIT,
          search: trimValue,
          type: REQUEST_TYPES?.LIBRARY_CLOSURE,
          dateRange: {
            from: finalWhereFilters?.createdAt?.from?.$d,
            to: finalWhereFilters?.createdAt?.to?.$d,
          },
          actionTakenOn: {
            from: finalWhereFilters?.actionTakenOn?.from?.$d,
            to: finalWhereFilters?.actionTakenOn?.to?.$d,
          },
          status:
            finalWhereFilters?.status?.length > 0
              ? finalWhereFilters?.status
              : undefined,
          libraryIds: finalWhereFilters?.name,
          backUpLibraryIds: finalWhereFilters?.backUpLibName,
          closureRequestToDateRange: {
            to: finalWhereFilters?.toDate?.date?.$d,
          },
          closureRequestFromDateRange: {
            from: finalWhereFilters?.fromDate?.date?.$d,
          },
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialAddressSort,
      },
    });
  };

  const handleRaiseRequest = async (values) => {
    setIsRaiseRequestLoading(true);
    const response = await createRequestLibrary({
      variables: {
        data: {
          closureRequest: {
            fromDate: values?.closureDate?.[0]?.$d,
            toDate: values?.closureDate?.[1]?.$d,
          },
          description: values?.reason,
          type: REQUEST_TYPES?.LIBRARY_CLOSURE,
        },
      },
    });
    if (response?.data) {
      setClosureReqList([]);
      setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
      setIsClosureReqLoading(true);
      setIsRaiseRequestModalOpen(false);
      form?.resetFields();
      requestsLibrary({
        variables: {
          filter: {
            ...initialAddressFilter,
            limit: paginationProp?.pageSize || LIMIT,
            search: closureReqSearchTerm,
            type: REQUEST_TYPES?.LIBRARY_CLOSURE,
            dateRange: {
              from: finalWhereFilters?.createdAt?.from?.$d,
              to: finalWhereFilters?.createdAt?.to?.$d,
            },
            actionTakenOn: {
              from: finalWhereFilters?.actionTakenOn?.from?.$d,
              to: finalWhereFilters?.actionTakenOn?.to?.$d,
            },
            status:
              finalWhereFilters?.status?.length > 0
                ? finalWhereFilters?.status
                : undefined,
            libraryIds: finalWhereFilters?.name,
            backUpLibraryIds: finalWhereFilters?.backUpLibName,
            closureRequestToDateRange: {
              to: finalWhereFilters?.toDate?.date?.$d,
            },
            closureRequestFromDateRange: {
              from: finalWhereFilters?.fromDate?.date?.$d,
            },
          },
          sort: sortedInfo?.column
            ? {
                field: sortedInfo?.field,
                order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
              }
            : initialAddressSort,
        },
      });
    }
    setIsRaiseRequestLoading(false);
  };

  const handleFilter = (values) => {
    setIsClosureReqLoading(true);
    setIsAllFilterModalOpen(false);
    setClosureReqList([]);
    setPaginationProp({ ...paginationProp, current: 1, skip: 0 });
    setIsActive(true);
    const filters = {
      status: values?.status,
      createdAt: values?.createdAt?.[1]
        ? { from: values?.createdAt?.[0], to: values?.createdAt?.[1] }
        : null,
      actionTakenOn: values?.actionTakenOn?.[1]
        ? { from: values?.actionTakenOn?.[0], to: values?.actionTakenOn?.[1] }
        : null,
      fromDate: values?.fromDate ? { date: values?.fromDate } : null,
      toDate: values?.toDate ? { date: values?.toDate } : null,
    };
    setWhereFilter(filters);
    setFinalWhereFilters(filters);
    requestsLibrary({
      variables: {
        filter: {
          ...initialAddressFilter,
          limit: paginationProp?.pageSize || LIMIT,
          search: closureReqSearchTerm,
          type: REQUEST_TYPES?.LIBRARY_CLOSURE,
          dateRange: {
            from: values?.createdAt?.[0],
            to: values?.createdAt?.[1],
          },
          actionTakenOn: {
            from: values?.actionTakenOn?.[0],
            to: values?.actionTakenOn?.[1],
          },
          status: values?.status,
          closureRequestToDateRange: {
            to: values?.toDate,
          },
          closureRequestFromDateRange: {
            from: values?.fromDate,
          },
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialAddressSort,
      },
    });
  };

  const handleClosureRequestScroll = () => {
    if (!isFetchMoreClosureReqLoading && !isClosureReqLoading) {
      const currentLength = closureReqList?.length;
      setIsFetchMoreClosureReqLoading(true);
      requestsLibrary({
        variables: {
          filter: {
            ...initialAddressFilter,
            skip: currentLength,
            limit: paginationProp?.pageSize || LIMIT,
            search: closureReqSearchTerm,
            type: REQUEST_TYPES?.LIBRARY_CLOSURE,
            dateRange: {
              from: finalWhereFilters?.createdAt?.from?.$d,
              to: finalWhereFilters?.createdAt?.to?.$d,
            },
            actionTakenOn: {
              from: finalWhereFilters?.actionTakenOn?.from?.$d,
              to: finalWhereFilters?.actionTakenOn?.to?.$d,
            },
            status:
              finalWhereFilters?.status?.length > 0
                ? finalWhereFilters?.status
                : undefined,
            closureRequestToDateRange: {
              to: finalWhereFilters?.toDate?.date?.$d,
            },
            closureRequestFromDateRange: {
              from: finalWhereFilters?.fromDate?.date?.$d,
            },
          },
          sort: sortedInfo?.column
            ? {
                field: sortedInfo?.field,
                order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
              }
            : initialAddressSort,
        },
      });
    }
  };

  // eslint-disable-next-line react-hooks/rules-of-hooks, no-use-before-define
  const infiniteScrollRef = useInfiniteScroll(
    handleClosureRequestScroll,
    isActive,
  );

  const getFilterData = (confirm) => {
    setIsClosureReqLoading(true);
    requestsLibrary({
      variables: {
        filter: {
          ...initialAddressFilter,
          limit: paginationProp?.pageSize || LIMIT,
          search: closureReqSearchTerm,
          type: REQUEST_TYPES?.LIBRARY_CLOSURE,
          dateRange: {
            from: whereFilter?.createdAt?.from?.$d,
            to: whereFilter?.createdAt?.to?.$d,
          },
          actionTakenOn: {
            from: whereFilter?.actionTakenOn?.from?.$d,
            to: whereFilter?.actionTakenOn?.to?.$d,
          },
          status:
            whereFilter?.status?.length > 0 ? whereFilter?.status : undefined,
          libraryIds: whereFilter?.name,
          backUpLibraryIds: whereFilter?.backUpLibName,
          closureRequestToDateRange: {
            to: whereFilter?.toDate?.date?.$d,
          },
          closureRequestFromDateRange: {
            from: whereFilter?.fromDate?.date?.$d,
          },
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialAddressSort,
      },
    });
    setFinalWhereFilters({ ...finalWhereFilters, ...whereFilter });
    setFinalFilterTags(filterTags);
    setPaginationProp({ ...paginationProp, skip: 0, current: 1 });
    if (confirm) {
      confirm();
    }
  };

  const handleReset = (clearFilters, dataIndex, confirm) => {
    const tagsCopy = {
      ...filterTags,
      [dataIndex]: [],
    };
    const whereFiltersCopy = {
      ...whereFilter,
      [dataIndex]: [],
    };
    setFilterVisible(false);
    setFinalFilterTags({ ...filterTags, ...tagsCopy });
    setFinalWhereFilters({ ...finalWhereFilters, ...whereFiltersCopy });
    setIsClosureReqLoading(true);
    requestsLibrary({
      variables: {
        filter: {
          ...initialAddressFilter,
          limit: paginationProp?.pageSize || LIMIT,
          search: closureReqSearchTerm,
          type: REQUEST_TYPES?.LIBRARY_CLOSURE,
          dateRange: {
            from: whereFiltersCopy?.createdAt?.from?.$d,
            to: whereFiltersCopy?.createdAt?.to?.$d,
          },
          actionTakenOn: {
            from: whereFiltersCopy?.actionTakenOn?.from?.$d,
            to: whereFiltersCopy?.actionTakenOn?.to?.$d,
          },
          status:
            whereFiltersCopy?.status?.length > 0
              ? whereFiltersCopy?.status
              : undefined,
          libraryIds: whereFiltersCopy?.name,
          backUpLibraryIds: whereFiltersCopy?.backUpLibName,
          closureRequestToDateRange: {
            to: whereFiltersCopy?.toDate?.date?.$d,
          },
          closureRequestFromDateRange: {
            from: whereFiltersCopy?.fromDate?.date?.$d,
          },
        },
        sort: sortedInfo?.column
          ? {
              field: sortedInfo?.field,
              order: sortedInfo?.order === 'ascend' ? SORT?.ASC : SORT?.DESC,
            }
          : initialAddressSort,
      },
    });
    setPaginationProp({ ...paginationProp, skip: 0, current: 1 });
    clearFilters();
    setFilterVisible(false);
    if (confirm) {
      confirm();
    }
  };

  useEffect(() => {
    if (filterVisible) {
      setFilterList([]);
      setFilterLoading(true);
      setIsEmptyFilterList(false);
      setFilterTags({ ...filterTags, ...finalFilterTags });
      setWhereFilter({ ...whereFilter, ...finalWhereFilters });
      const optionsCopy = [];

      switch (filterIndex) {
        case 'status':
          forEach(STATUS_FILTER, (item) => {
            optionsCopy?.push({ label: item?.label, key: item?.key });
          });
          setFilterList(optionsCopy);
          setFilterLoading(false);
          break;

        default:
          break;
      }
    }
    if (!filterVisible) {
      setFilterTags(finalFilterTags);
      setWhereFilter(finalWhereFilters);
    }
  }, [filterVisible]);

  const changeFilter = (newFilterObj, dataIndex) => {
    const {
      target: { value },
    } = newFilterObj;

    const filterTagsCopy = { ...filterTags };
    let newTagList;
    if (whereFilter?.[dataIndex]?.includes(value?.key)) {
      newTagList = {
        ...filterTagsCopy,
        [dataIndex]: filter(filterTagsCopy?.[dataIndex], (item) => {
          if (item?.label) {
            return item?.key !== value?.key;
          }
          return item !== value;
        }),
      };
    } else {
      newTagList = {
        ...filterTagsCopy,
        [dataIndex]: filterTagsCopy?.[dataIndex]
          ? [...filterTagsCopy?.[dataIndex], value]
          : [value],
      };
    }

    const updatedWhereFilter = {
      ...whereFilter,
      [dataIndex]: newTagList?.[dataIndex]?.map((item) => item?.key),
    };
    setWhereFilter(updatedWhereFilter);
    setFilterTags(newTagList);
  };

  const handleDeleteFilter = (value, dataIndex) => {
    let newTagList;

    if (whereFilter?.[dataIndex]?.includes(value?.key)) {
      newTagList = {
        ...filterTags,
        [dataIndex]: filter(filterTags?.[dataIndex], (item) => {
          if (item?.key) {
            return item?.key !== value?.key;
          }
          return item !== value;
        }),
      };
    }

    const updatedWhereFilter = {
      ...whereFilter,
      [dataIndex]: newTagList?.[dataIndex]?.map((item) => item?.key),
    };
    setWhereFilter(updatedWhereFilter);
    setFilterTags(newTagList);
  };

  const onRangePickerChange = (values, dataIndex) => {
    const rangeObj = {
      from: values[0]?.startOf('day'),
      to: values?.[1]?.endOf('day'),
    };

    const filtersCopy = {
      ...whereFilter,
      [dataIndex]: rangeObj,
    };
    if (!values?.length) {
      delete whereFilter?.[dataIndex];
    }
    setWhereFilter(filtersCopy);
  };

  const onDatePickerChange = (values, dataIndex) => {
    let date;
    if (dataIndex === 'fromDate') {
      date = {
        date: values?.startOf('day'),
      };
    }
    if (dataIndex === 'toDate') {
      date = {
        date: values?.endOf('day'),
      };
    }

    const filtersCopy = {
      ...whereFilter,
      [dataIndex]: date,
    };
    if (!values?.length) {
      delete whereFilter?.[dataIndex];
    }
    setWhereFilter(filtersCopy);
  };

  const filterPopup = (dataIndex) => ({
    filterDropdown: ({ confirm, clearFilters }) => {
      if (dataIndex === 'createdAt' || dataIndex === 'actionTakenOn') {
        return (
          <div className="custom-filter-dropdown">
            <RangePicker
              allowClear={false}
              format={DATE_FORMATS?.REGULAR}
              onChange={(values) => onRangePickerChange(values, dataIndex)}
              value={
                whereFilter?.[dataIndex]?.from
                  ? [
                      whereFilter?.[dataIndex]?.from,
                      whereFilter?.[dataIndex]?.to,
                    ]
                  : null
              }
            />
            <Divider className="divider-filter" />
            <div className="d-flex justify-center">
              <Button
                size="small"
                className="reset-button"
                id="roles-filter-reset"
                onClick={() => handleReset(clearFilters, dataIndex, confirm)}
              >
                Reset
              </Button>
              <Button
                size="small"
                className="common-button ok-button"
                id="roles-filter-ok"
                type="primary"
                onClick={() => getFilterData(confirm, dataIndex)}
              >
                Ok
              </Button>
            </div>
          </div>
        );
      }

      if (dataIndex === 'fromDate' || dataIndex === 'toDate') {
        return (
          <div className="custom-filter-dropdown">
            <DatePicker
              allowClear={false}
              format={DATE_FORMATS?.REGULAR}
              onChange={(values) => onDatePickerChange(values, dataIndex)}
              value={
                whereFilter?.[dataIndex]?.date
                  ? whereFilter?.[dataIndex]?.date
                  : null
              }
              className="full-width"
            />
            <Divider className="divider-filter" />
            <div className="d-flex justify-center">
              <Button
                size="small"
                className="reset-button"
                id="roles-filter-reset"
                onClick={() => handleReset(clearFilters, dataIndex, confirm)}
              >
                Reset
              </Button>
              <Button
                size="small"
                className="common-button ok-button"
                id="roles-filter-ok"
                type="primary"
                onClick={() => getFilterData(confirm, dataIndex)}
              >
                Ok
              </Button>
            </div>
          </div>
        );
      }

      return (
        <div className="custom-filter-dropdown">
          <LoaderComponent
            spinning={filterLoading}
            setHeight={35}
            size="medium"
          >
            {filterTags?.[dataIndex]?.length > 0 && (
              <div className="filter-tags-wrapper">
                {map(filterTags?.[dataIndex], (item) => (
                  <Tag
                    key={item?.key}
                    closable
                    onClose={() => handleDeleteFilter(item, dataIndex)}
                    className="filter-tag"
                  >
                    <span>{item?.label}</span>
                  </Tag>
                ))}
              </div>
            )}
            <div className="filter-checkboxes">
              {filterList?.length > 0
                ? map(filterList, (item) => (
                    <div className="single-checkbox-div" key={item?.key}>
                      <Checkbox
                        value={item}
                        checked={whereFilter?.[dataIndex]?.includes(item?.key)}
                        key={item?.key || item}
                        onChange={(e) => changeFilter(e, dataIndex)}
                        className="single-checkbox"
                      >
                        <span>{item?.label}</span>
                      </Checkbox>
                    </div>
                  ))
                : isEmptyFilterList && (
                    <Empty image={Empty?.PRESENTED_IMAGE_SIMPLE} />
                  )}
            </div>
          </LoaderComponent>
          <Divider className="divider-filter" />
          <div className="d-flex justify-center">
            <Button
              size="small"
              className="reset-button"
              id="roles-filter-reset"
              onClick={() => handleReset(clearFilters, dataIndex, confirm)}
            >
              Reset
            </Button>
            <Button
              size="small"
              className="common-button ok-button"
              id="roles-filter-ok"
              type="primary"
              onClick={() => getFilterData(confirm, dataIndex)}
            >
              Ok
            </Button>
          </div>
        </div>
      );
    },
    filterIcon: () =>
      finalWhereFilters?.[dataIndex]?.length > 0 ||
      !isEmpty(finalWhereFilters?.[dataIndex]) ? (
        <FilterFilled className="filter-icon filter-filled" />
      ) : (
        <FilterOutlined className="filter-icon" />
      ),
    onFilterDropdownOpenChange: (visible) => {
      setFilterIndex(dataIndex);
      setFilterVisible(visible);
    },
  });

  const columns = [
    {
      title: 'REQUEST ID',
      dataIndex: 'id',
      key: 'id',
      ellipsis: true,
      width: 170,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => record?.requestNumber,
    },
    {
      title: 'REQUEST DATE',
      dataIndex: 'requestDate',
      key: 'requestDate',
      ellipsis: true,
      width: 150,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) =>
        formatDate(record?.createdAt, DATE_FORMATS?.REGULAR) || '-',
      ...filterPopup('createdAt'),
    },
    {
      title: 'FROM DATE',
      dataIndex: 'fromDate',
      key: 'fromDate',
      ellipsis: true,
      width: 150,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) =>
        formatDate(record?.metaData?.fromDate, DATE_FORMATS?.REGULAR) || '-',
      ...filterPopup('fromDate'),
    },
    {
      title: 'TO DATE',
      dataIndex: 'toDate',
      key: 'toDate',
      ellipsis: true,
      width: 150,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) =>
        formatDate(record?.metaData?.toDate, DATE_FORMATS?.REGULAR) || '-',
      ...filterPopup('toDate'),
    },
    {
      title: 'REASON',
      dataIndex: 'reason',
      key: 'reason',
      ellipsis: true,
      width: 250,
      align: 'left',
      className: 'max-width-column table-tooltip-cell',
      render: (_, record) => (
        <Tooltip
          title={record?.description ? record?.description : 'No Description'}
          placement="topLeft"
          trigger="click"
          className="pointer"
        >
          {record?.description || '-'}
        </Tooltip>
      ),
    },
    {
      title: 'ACTION TAKEN ON',
      dataIndex: 'actionTakenOn',
      key: 'actionTakenOn',
      ellipsis: true,
      width: 150,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) =>
        formatDate(record?.updatedAt, DATE_FORMATS?.REGULAR) || '-',
      ...filterPopup('actionTakenOn'),
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status',
      ellipsis: true,
      width: 150,
      align: 'left',
      className: 'max-width-column',
      render: (_, record) => (
        <Badge
          color={REQUEST_STATUS?.[record?.status]?.color}
          text={REQUEST_STATUS?.[record?.status]?.text}
        />
      ),
      ...filterPopup('status'),
    },
  ];

  const locale = {
    emptyText: isEmptyClosureReqList ? '' : <span />,
  };

  return (
    <>
      <RaiseClosureRequestModal
        form={form}
        onFinish={handleRaiseRequest}
        loadings={isRaiseRequestLoading}
        isModalOpen={isRaiseRequestModalOpen}
        setIsModalOpen={setIsRaiseRequestModalOpen}
      />
      <div className="d-flex justify-end mb-16 align-center">
        <SearchComponent
          className={isDesktop ? 'search-box' : ''}
          query={query}
          setQuery={setQuery}
          getData={handleSearch}
        />
        {!isDesktop && (
          <Badge dot={hasNonEmptyValueObj(finalWhereFilters)} size="small">
            <FilterIcon
              className="mobile-svg-icon ml-16"
              onClick={() => setIsAllFilterModalOpen(true)}
            />
          </Badge>
        )}
      </div>
      {isDesktop ? (
        <CommonTable
          locale={locale}
          columns={columns}
          data={closureReqList || []}
          loading={isClosureReqLoading}
          onChange={handleTableChange}
          paginationConfig={paginationProp}
          rowKey={(record) => record?.id}
        />
      ) : (
        <div className="mobile-container-tab no-scroll-bar">
          <AllFilterClosureReqModal
            onFinish={handleFilter}
            isModalOpen={isAllFilterModalOpen}
            setIsModalOpen={setIsAllFilterModalOpen}
            finalWhereFilters={finalWhereFilters}
            setFinalWhereFilters={setFinalWhereFilters}
          />
          <ClosureRequestList
            closureReqList={closureReqList}
            infiniteScrollRef={infiniteScrollRef}
            isFetchMoreClosureReqLoading={isFetchMoreClosureReqLoading}
            isEmptyClosureReqList={isEmptyClosureReqList}
            isClosureReqLoading={isClosureReqLoading}
          />
        </div>
      )}
    </>
  );
};

export default ClosureRequestsTable;
